'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
type Placement = 'start' | 'end' | 'bottom';
type Size = 'sm' | 'md' | 'lg' | 'full';
const SIZE_MAP: Record<Size, string> = {
  sm: '320px',
  md: '400px',
  lg: '500px',
  full: '100vw'
};
const BOTTOM_HEIGHT_MAP: Record<Size, string> = {
  sm: '40vh',
  md: '60vh',
  lg: '80vh',
  full: '100vh'
};
export function Drawer(t0) {
  const $ = _c(27);
  const {
    isOpen,
    onOpenChange,
    placement: t1,
    size: t2,
    children,
    className: t3,
    "aria-label": t4
  } = t0;
  const placement = t1 === undefined ? "end" : t1;
  const size = t2 === undefined ? "md" : t2;
  const className = t3 === undefined ? "" : t3;
  const ariaLabel = t4 === undefined ? "" : t4;
  let t5;
  if ($[0] !== onOpenChange) {
    t5 = () => onOpenChange(false);
    $[0] = onOpenChange;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const close = t5;
  let t6;
  let t7;
  if ($[2] !== close || $[3] !== isOpen) {
    t6 = () => {
      if (!isOpen) {
        return;
      }
      const h = e => {
        if (e.key === "Escape") {
          close();
        }
      };
      document.addEventListener("keydown", h);
      return () => document.removeEventListener("keydown", h);
    };
    t7 = [isOpen, close];
    $[2] = close;
    $[3] = isOpen;
    $[4] = t6;
    $[5] = t7;
  } else {
    t6 = $[4];
    t7 = $[5];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[6] !== isOpen) {
    t8 = () => {
      if (!isOpen) {
        return;
      }
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    };
    t9 = [isOpen];
    $[6] = isOpen;
    $[7] = t8;
    $[8] = t9;
  } else {
    t8 = $[7];
    t9 = $[8];
  }
  useEffect(t8, t9);
  if (typeof window === "undefined" || !isOpen) {
    return null;
  }
  const isBottom = placement === "bottom";
  const isStart = placement === "start";
  const panelClasses = isBottom ? `fixed inset-x-0 bottom-0 z-50 flex flex-col bg-background shadow-xl rounded-t-2xl ${className}` : `fixed top-0 ${isStart ? "inset-inline-start-0" : "inset-inline-end-0"} z-50 h-full flex flex-col bg-background shadow-xl ${className}`;
  let t10;
  if ($[9] !== isBottom || $[10] !== isStart || $[11] !== size) {
    t10 = isBottom ? {
      maxHeight: BOTTOM_HEIGHT_MAP[size],
      width: "100%",
      animation: "slideInBottom 0.2s ease-out"
    } : {
      maxWidth: SIZE_MAP[size],
      width: size === "full" ? "100vw" : "92vw",
      animation: `${isStart ? "slideInStart" : "slideInEnd"} 0.2s ease-out`
    };
    $[9] = isBottom;
    $[10] = isStart;
    $[11] = size;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const panelStyle = t10;
  let t11;
  if ($[13] !== close) {
    t11 = <div className="fixed inset-0 z-50 bg-black/40 animate-[fadeIn_0.15s_ease-out]" onClick={close} aria-hidden="true" />;
    $[13] = close;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== isBottom) {
    t12 = isBottom && <div className="flex justify-center pt-2 pb-1 shrink-0"><span className="block h-1 w-10 rounded-full bg-border" aria-hidden="true" /></div>;
    $[15] = isBottom;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== ariaLabel || $[18] !== children || $[19] !== panelClasses || $[20] !== panelStyle || $[21] !== placement || $[22] !== t12) {
    t13 = <div className={panelClasses} style={panelStyle} data-drawer-placement={placement} role="dialog" aria-modal="true" aria-label={ariaLabel}>{t12}{children}</div>;
    $[17] = ariaLabel;
    $[18] = children;
    $[19] = panelClasses;
    $[20] = panelStyle;
    $[21] = placement;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t11 || $[25] !== t13) {
    t14 = createPortal(<>{t11}{t13}</>, document.body);
    $[24] = t11;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  return t14;
}
export function DrawerHeader(t0) {
  const $ = _c(3);
  const {
    children,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t2 = `shrink-0 ${className}`;
  let t3;
  if ($[0] !== children || $[1] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[0] = children;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export function DrawerBody(t0) {
  const $ = _c(3);
  const {
    children,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t2 = `flex-1 overflow-y-auto ${className}`;
  let t3;
  if ($[0] !== children || $[1] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[0] = children;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export function DrawerFooter(t0) {
  const $ = _c(3);
  const {
    children,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t2 = `shrink-0 ${className}`;
  let t3;
  if ($[0] !== children || $[1] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[0] = children;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export function DrawerContent(t0) {
  const $ = _c(3);
  const {
    children,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t2 = `flex flex-col h-full ${className}`;
  let t3;
  if ($[0] !== children || $[1] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[0] = children;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}